import { ObjectiveWorkflowState } from '@/api/models/aggregate'
import { IFilter, PriorityFilterValue, ProgressFilterValue } from '@/components/CourseFiltersDrawer/types'
import { useAppSelector } from '@/store/hooks'
import { createFiltersSelector } from '@/store/slices/aggregatesSlice'

export const ENROLLED_COURSES_FILTER = `[
  ['objective.activity.state', '<>', 4], "and",
  [
    ["objectiveWorkflowAggregate.isSelfEnrolled", "=", "true"], "or", 
    ["isMandatory", "=", "true"], "or",
    ["objectiveWorkflowAggregate.lastObjectiveWorkflow.id", "<>", null], "or",
    ["objectiveWorkflowAggregate.lastObjectiveRecord.id", "<>", null]
  ]
]`

export const useGetEnrolledCoursesFilter = (): string => {
  const searchText = useAppSelector((state) => state.aggregates.enrolledCoursesSearchText)
  const filters = useAppSelector((state) => createFiltersSelector(state, 'enrolled'))

  const getSearchFilter = (text: string): string => {
    if (!text) return ''

    return `[
      ["objective.code", "contains", "${text}"], "or",
      ["objective.name", "contains", "${text}"], "or",
      ["objective.description", "contains", "${text}"]
    ]`
  }

  const buildFilter = (filters: IFilter): string => {
    const buildPriorityFilter = (priority: PriorityFilterValue): string =>
      `["isMandatory", "=", "${priority === PriorityFilterValue.MANDATORY}"]`

    // hasWorkflow means lastObjectiveWorkflow is not null
    // hasRecord means lastObjectiveRecord is not null

    const buildProgressFilter = (progress: ProgressFilterValue): string => {
      switch (progress) {
        case ProgressFilterValue.NOT_STARTED:
          // since exported courses have no workflow, but have records, we need to exclude them
          return `[["objectiveWorkflowAggregate.lastObjectiveWorkflow.id", "=", null], "and", ["objectiveWorkflowAggregate.lastObjectiveRecord.id", "=", null]]`
        case ProgressFilterValue.IN_PROGRESS:
          return `[
            ["objectiveWorkflowAggregate.lastObjectiveWorkflow.state", "=", "${ObjectiveWorkflowState.inProgress}"],
            "or", ["objectiveWorkflowAggregate.lastObjectiveWorkflow.state", "=", "${ObjectiveWorkflowState.inGrading}"],
            "or", ["objectiveWorkflowAggregate.lastObjectiveWorkflow.state", "=", "${ObjectiveWorkflowState.gradingApproved}"]
          ]`
        case ProgressFilterValue.COMPLETED:
          // since exported courses have no workflow, but have records, we need to include them
          return `[
            [
              "objectiveWorkflowAggregate.lastObjectiveWorkflow.state",
              "=",
              "${ObjectiveWorkflowState.finished}"
            ],
            "or",
            [
              [
                "objectiveWorkflowAggregate.lastObjectiveWorkflow.id",
                "=",
                null
              ],
              "and",
              [
                "objectiveWorkflowAggregate.lastObjectiveRecord.id",
                "<>",
                null
              ]
            ]
          ]`
        default:
          return ''
      }
    }

    const buildTermFilter = (term: string): string => `["termId", "=", "${term}"]`

    const buildFilters = (queries: string[]): string =>
      queries
        .filter(Boolean)
        .map((filter, index, array) => (index === array.length - 1 ? `${filter}` : `${filter}, "or"`))
        .join(', ')

    const priorityFilters = buildFilters(filters.coursePriority.map(buildPriorityFilter))
    const progressFilters = buildFilters(filters.courseProgress.map(buildProgressFilter))
    const termFilters = buildFilters(filters.term.map(buildTermFilter))

    const combinedFilters = [priorityFilters, progressFilters, termFilters]
      .filter(Boolean)
      .map((filter, index, array) => (index === array.length - 1 ? `[${filter}]` : `[${filter}], "and"`))

    return combinedFilters.join(', ')
  }

  const searchFilter = getSearchFilter(searchText)
  const filtersQuery = buildFilter(filters)

  const finalFilter = [searchFilter, ENROLLED_COURSES_FILTER, filtersQuery].filter(Boolean).join(', "and", ')

  return `[${finalFilter}]`
}
